import { __rest } from "tslib";
import { useListState } from '@sas/nova-commons/components/List/useListState';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Divider } from '@sas/nova-commons/components/Divider';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { Add, Location, Overflow } from '@sas/react-svg';
import { GroupHeaderItem, Item, List } from '@sas/nova-commons/components/List';
import { MenuButton } from '@sas/nova-commons/components/MenuButton';
import { Popover } from '@sas/nova-commons/components/Popover';
import { Heading } from '@sas/nova-commons/components/Heading';
import { useDesignTokens, renderInlineCssVars } from '@sas/nova-commons/theme';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../../../locale/I18nContext';
import { PanelListItem, panelItemHeight } from '../components/PanelListItem';
import { ObjectSearchState, isSearchHint, isSearchResult, isGeographicFeature, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { Demographics } from '../GeographicAreaPanel/Demographics';
import { AutoFocusInside } from 'react-focus-lock';
import cn from './MainSearchPanel.css.json';
import './MainSearchPanel.css';
/**
 * Returns {true} if the given list item is a {PanelListItem}
 */
const isPanelListItem = (object) => {
    return 'item' in object;
};
const useMainSearchPanelId = createUseHtmlId('MainSearchPanel');
export const MainSearchPanelBase = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    // Hooks
    // =============================================================================
    const localize = useI18n();
    const dt = useDesignTokens();
    const popoverHeadingId = useMainSearchPanelId();
    // Local states
    // ===============================================================================
    const [newMapFeaturePopoverOpen, setNewMapFeaturePopoverOpen] = useState(false);
    const newMapFeatureButtonReference = useRef(null);
    const [demographicsDialogState, setDemographicsDialogState] = useState({
        featureIndex: -1,
        resultsDialogIsOpen: false,
        selectorDialogIsOpen: false,
    });
    const selectedSearchItems = (_b = (_a = props.searchResults) === null || _a === void 0 ? void 0 : _a.filter((item) => item.selected)) !== null && _b !== void 0 ? _b : [];
    const selectedFeatureItems = (_d = (_c = props.features) === null || _c === void 0 ? void 0 : _c.filter((item) => item.selected)) !== null && _d !== void 0 ? _d : [];
    const _h = useListState(), { setSelectRows, clearMeasurementCache } = _h, listState = __rest(_h, ["setSelectRows", "clearMeasurementCache"]);
    /**
     * Returns the index for the given item state
     */
    const getItemIndex = useMemo(() => {
        return (itemState) => {
            if (props.searchHints && isSearchHint(itemState)) {
                return props.searchHints.indexOf(itemState);
            }
            if (props.searchResults && isSearchResult(itemState)) {
                return props.searchResults.indexOf(itemState);
            }
            if (props.features && isGeographicFeature(itemState)) {
                return props.features.indexOf(itemState);
            }
            return -1;
        };
    }, [props.searchHints, props.searchResults, props.features]);
    /**
     * Updates the visible property for the given item states
     */
    const onVisibilityStateChange = (searchItems, featureItems, visible) => {
        props.actions.notifyVisibilityChange(props.id, {
            searchResults: searchItems.map(getItemIndex),
            features: featureItems.map(getItemIndex),
            value: visible,
        });
    };
    /**
     * Builds the UI model for the main item list
     */
    const items = useMemo(() => {
        var _a, _b, _c;
        const onNavigationButtonPress = (itemState) => {
            const index = getItemIndex(itemState);
            props.actions.notifyUpdateSelection(props.id, itemState.type === 'result'
                ? { searchResults: [index], navigate: true }
                : { features: [index], navigate: true });
        };
        const items = [];
        if ((_a = props.searchHints) === null || _a === void 0 ? void 0 : _a.length) {
            items.push(...props.searchHints.map((item) => {
                return {
                    item: item,
                    onNavigationButtonPress,
                };
            }));
        }
        if ((_b = props.searchResults) === null || _b === void 0 ? void 0 : _b.length) {
            items.push({
                title: localize({ key: 'MainSearchPanel.searchResults.txt' }),
                className: cn.groupItemHeader,
            }, ...props.searchResults.map((item, index) => {
                return {
                    item: item,
                    counter: index + 1,
                    onVisibilityButtonPress: () => props.actions.notifyUpdateSearchResult(props.id, {
                        index,
                        update: { hidden: !item.hidden },
                    }),
                    onNavigationButtonPress,
                };
            }));
        }
        if ((_c = props.features) === null || _c === void 0 ? void 0 : _c.length) {
            items.push({
                title: localize({ key: 'MainSearchPanel.features.txt' }),
                className: cn.groupItemHeader,
            }, ...props.features.map((item, index) => {
                return Object.assign(Object.assign({ item: item, onVisibilityButtonPress: (item) => {
                        if (isSearchHint(item)) {
                            return;
                        }
                        props.actions.notifyUpdatePin(props.id, { index, update: { hidden: !item.hidden } });
                    } }, (item.type === 'area' && {
                    onShowDemographicButtonPress: () => {
                        setDemographicsDialogState({
                            featureIndex: index,
                            selectorDialogIsOpen: false,
                            resultsDialogIsOpen: true,
                        });
                    },
                })), { onNavigationButtonPress });
            }));
        }
        return items;
    }, [
        props.id,
        props.searchResults,
        props.searchHints,
        props.features,
        props.actions,
        localize,
        getItemIndex,
        setDemographicsDialogState,
    ]);
    useEffect(() => {
        const selectedItemIndices = []; // list of indices indicating the selection state of an item
        // find selected items
        items.forEach((itemProp, index) => {
            if ('item' in itemProp && 'selected' in itemProp.item && itemProp.item['selected'] === true) {
                selectedItemIndices.push(String(index));
            }
        });
        setSelectRows({
            selectedRows: new Set(selectedItemIndices),
        });
        clearMeasurementCache({ startRowIndex: 0, startColumnIndex: 0 });
    }, [items, setSelectRows, clearMeasurementCache]);
    const spacingTokens = renderInlineCssVars({ 'spacing-m': dt.spacing.core.m }, 'mainSearch');
    const buttonTokens = renderInlineCssVars({ 'hover-bg': 'transparent' }, 'button');
    return (React.createElement("div", { className: cn.container, style: spacingTokens },
        React.createElement("div", { className: cn.header },
            React.createElement(Button, { className: cn.button, style: buttonTokens, variant: ButtonVariant.Icon, icon: { icon: Location, tooltip: localize({ key: 'MainSearchPanel.close.label.txt' }) }, onPress: props.onCollapse }),
            React.createElement(AutoFocusInside, { className: cn.searchFieldContainer },
                React.createElement(SearchField, { className: cn.searchField, onChange: (e) => props.actions.update(props.id, { searchTerm: e.value }), onCommit: (e) => props.actions.notifySearch(props.id, { term: e.value }), value: (_e = props.searchTerm) !== null && _e !== void 0 ? _e : '' }))),
        React.createElement(List, Object.assign({}, listState, { className: cn.list, selectionMode: "desktop", rowCount: items.length, expandToFillWidth: true, showSeparator: () => false, itemHeight: () => 'double', height: panelItemHeight * items.length, renderItem: ({ index }) => {
                const item = items[index];
                return isPanelListItem(item) ? (React.createElement(PanelListItem, Object.assign({}, item))) : (React.createElement(GroupHeaderItem, Object.assign({}, item)));
            }, onSelect: (selectedRows) => {
                if (!selectedRows) {
                    return;
                }
                const searchResults = [];
                const features = [];
                for (const itemIndex of Array.from(selectedRows)) {
                    const listItem = items[parseInt(itemIndex)];
                    if (isPanelListItem(listItem)) {
                        if (isSearchHint(listItem.item)) {
                            props.actions.notifySearch(props.id, {
                                term: listItem.item.name,
                                autoCreate: !listItem.item.isMultiple,
                            });
                            return;
                        }
                        if (isSearchResult(listItem.item)) {
                            const index = getItemIndex(listItem.item);
                            searchResults.push(index);
                        }
                        if (isGeographicFeature(listItem.item)) {
                            const index = getItemIndex(listItem.item);
                            features.push(index);
                            props.actions.update(props.id, { searchHints: [] });
                        }
                    }
                }
                props.actions.notifyUpdateSelection(props.id, {
                    searchResults,
                    features,
                });
            } })),
        demographicsDialogState.featureIndex >= 0 ? (React.createElement(Demographics, { id: props.id, dialogOpenState: demographicsDialogState, setDialogOpenState: setDemographicsDialogState })) : null,
        React.createElement(Divider, { variant: "standard" }),
        React.createElement("div", { className: cn.footer },
            React.createElement(Button, { className: cn.newFeatureButton, ref: newMapFeatureButtonReference, variant: ButtonVariant.Light, text: localize({ key: 'MainSearchPanel.newMapFeature.txt' }), icon: { icon: Add }, onPress: () => setNewMapFeaturePopoverOpen(!newMapFeaturePopoverOpen) }),
            React.createElement(Popover, { referenceElementRef: newMapFeatureButtonReference, isOpen: newMapFeaturePopoverOpen, onClose: () => setNewMapFeaturePopoverOpen(false), role: "dialog", ariaLabelledby: popoverHeadingId },
                React.createElement(Heading, { id: popoverHeadingId, level: 5, className: cn.newFeaturePopoverTitle, style: renderInlineCssVars({ fg: dt.c.colorTextPrimary, 'spacing-m': dt.spacing.core.m }, 'newFeaturePopoverTitle') }, localize({ key: 'MainSearchPanel.newMapFeature.title.txt' })),
                React.createElement(List, { rowCount: 3, expandToFillWidth: true, selectionMode: 'single', itemHeight: () => 'single', onSelect: (selectedRows) => {
                        var _a;
                        const actionMap = {
                            '0': () => props.actions.notifyNewPin(props.id),
                            '1': () => props.actions.notifyNewRoute(props.id),
                            '2': () => props.actions.notifyNewArea(props.id),
                        };
                        setNewMapFeaturePopoverOpen(false);
                        const selectedIndex = Array.from(selectedRows !== null && selectedRows !== void 0 ? selectedRows : [])[0];
                        (_a = actionMap[selectedIndex]) === null || _a === void 0 ? void 0 : _a.call(actionMap);
                        props.actions.update(props.id, { searchHints: [] });
                    }, height: 30 * 3, width: 200, renderItem: ({ index }) => {
                        let titleResourceKey;
                        switch (index) {
                            case 0:
                                titleResourceKey = 'MainSearchPanel.newPin.txt';
                                break;
                            case 1:
                                titleResourceKey = 'MainSearchPanel.newRoute.txt';
                                break;
                            case 2:
                                titleResourceKey = 'MainSearchPanel.newGeographicArea.txt';
                                break;
                            case 3:
                                titleResourceKey = 'MainSearchPanel.newMyLocationPin.txt';
                                break;
                        }
                        return React.createElement(Item, { title: titleResourceKey ? localize({ key: titleResourceKey }) : '' });
                    } })),
            React.createElement(MenuButton, { className: cn.overflowButton, disabled: !(((_f = props.features) === null || _f === void 0 ? void 0 : _f.length) || ((_g = props.searchResults) === null || _g === void 0 ? void 0 : _g.length)), onItemSelected: (location) => {
                    var _a, _b, _c;
                    let searchItems = selectedSearchItems;
                    let featureItems = selectedFeatureItems;
                    if (searchItems.length + featureItems.length === 0) {
                        // no selection so use all
                        searchItems = (_a = props.searchResults) !== null && _a !== void 0 ? _a : [];
                        featureItems = (_b = props.features) !== null && _b !== void 0 ? _b : [];
                    }
                    const section = location[0];
                    if (section.length === 2) {
                        if (section[0] === 0 && section[1] === 0) {
                            onVisibilityStateChange(searchItems, featureItems, true);
                        }
                        else if (section[0] === 0 && section[1] === 1) {
                            onVisibilityStateChange(searchItems, featureItems, false);
                        }
                        else if (section[0] === 0 && section[1] === 2) {
                            if (!selectedSearchItems.length && !selectedFeatureItems.length) {
                                if ((_c = props.searchResults) === null || _c === void 0 ? void 0 : _c.length) {
                                    props.actions.notifyDeleteSearchResults(props.id, props.searchResults.map(getItemIndex));
                                }
                                props.actions.notifyDeleteAllPins(props.id);
                            }
                            if (selectedSearchItems.length) {
                                props.actions.notifyDeleteSearchResults(props.id, selectedSearchItems.map(getItemIndex));
                            }
                            if (selectedFeatureItems.length) {
                                props.actions.notifyDeletePins(props.id, selectedFeatureItems.map(getItemIndex));
                            }
                            // } else if (section[0] === 1 && section[1] === 0) {
                            //   // show pin labels
                            //   // TODO: implement
                        }
                        else if (section[0] === 1 && section[1] === 0) {
                            if (selectedSearchItems.length > 0) {
                                // convert selected search results
                                props.actions.notifyConvertSearchResults(props.id, selectedSearchItems.map(getItemIndex));
                            }
                            else {
                                // convert all search result to pin
                                props.actions.notifyConvertAllSearchResults(props.id);
                            }
                        }
                    }
                }, sections: [
                    {
                        items: [
                            {
                                text: localize({
                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                        ? 'MainSearchPanel.show.txt'
                                        : 'MainSearchPanel.show.all.txt',
                                }),
                            },
                            {
                                text: localize({
                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                        ? 'MainSearchPanel.hide.txt'
                                        : 'MainSearchPanel.hide.all.txt',
                                }),
                            },
                            {
                                text: localize({
                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                        ? 'MainSearchPanel.delete.txt'
                                        : 'MainSearchPanel.delete.all.txt',
                                }),
                            },
                        ],
                    },
                    // {
                    //   items: [
                    //       {
                    //         text: localize({ key: 'MainSearchPanel.showPinLabels.txt' }),
                    //         disabled: props.features?.filter((item) => item?.type === 'pin').length === 0
                    //       }
                    //   ],
                    // },
                    {
                        items: [
                            {
                                disabled: !props.searchResults || props.searchResults.length === 0,
                                text: localize({
                                    key: selectedSearchItems.length > 0
                                        ? 'MainSearchPanel.convert.txt'
                                        : 'MainSearchPanel.convert.all.txt',
                                }),
                            },
                        ],
                    },
                ], variant: ButtonVariant.Light, icon: { icon: Overflow } }))));
};
export const MainSearchPanel = connect((state, props) => {
    var _a;
    const { features, searchTerm, searchResults, searchHints, canDoPremiumServices } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    return {
        searchTerm,
        searchHints,
        searchResults,
        features,
        canDoPremiumServices,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(MainSearchPanelBase);
